import { useCallback, useEffect, useState } from 'react'

/** Whether a box overflows, and which of its edges have content scrolled past them. */
type ScrollEdges = {
  /** `true` while the content is taller than the box, in any scroll position. */
  scrollable: boolean
  /** `true` once the box has scrolled away from the top. */
  scrolledTop: boolean
  /** `true` while there is still content below the visible box. */
  scrolledBottom: boolean
}

const NO_SCROLL_EDGES: ScrollEdges = { scrollable: false, scrolledTop: false, scrolledBottom: false }

// `clientHeight`/`scrollHeight` are rounded integers while `scrollTop` is fractional at non-100%
// zoom/display scaling, so the true bottom can land a fraction short of the sum. A 1px tolerance
// keeps that from pinning `scrolledBottom` forever.
const BOTTOM_TOLERANCE_PX = 1

function measure(element: HTMLElement): ScrollEdges {
  return {
    scrollable: element.scrollHeight - element.clientHeight > BOTTOM_TOLERANCE_PX,
    scrolledTop: element.scrollTop > 0,
    scrolledBottom: element.scrollHeight - element.scrollTop - element.clientHeight > BOTTOM_TOLERANCE_PX,
  }
}

/**
 * Tracks whether an element overflows and which of its edges currently have content scrolled past
 * them, so a consumer can render a divider only where content is actually hidden, and expose the
 * box to the keyboard only when there is something to scroll. Recomputes on mount, on scroll,
 * whenever the element resizes, and whenever its content changes size without the element itself
 * resizing (e.g. a validation error appearing inside a Body already at its max height).
 *
 * @returns a callback ref to attach to the scrollable element, plus the current edge state.
 */
function useScrollEdges<T extends HTMLElement>(): { ref: (node: T | null) => void } & ScrollEdges {
  const [element, setElement] = useState<T | null>(null)
  const [edges, setEdges] = useState<ScrollEdges>(NO_SCROLL_EDGES)

  useEffect(
    function observeScrollEdges() {
      if (element === null) return

      const handleScroll = () => setEdges(measure(element))
      element.addEventListener('scroll', handleScroll, { passive: true })

      const resizeObserver = new ResizeObserver(() => setEdges(measure(element)))
      resizeObserver.observe(element)

      // The element's own box can be fixed (e.g. the Body at its `max-h` ceiling) while its content
      // still grows or shrinks — that changes `scrollHeight` without firing a resize.
      const mutationObserver = new MutationObserver(() => setEdges(measure(element)))
      mutationObserver.observe(element, { childList: true, subtree: true, characterData: true })

      return () => {
        element.removeEventListener('scroll', handleScroll)
        resizeObserver.disconnect()
        mutationObserver.disconnect()
      }
    },
    [element],
  )

  // Measured from the ref callback (commit phase, not an effect) so the initial edges are known
  // before paint, without a synchronous setState inside the effect above.
  const ref = useCallback((node: T | null) => {
    setElement(node)
    setEdges(node === null ? NO_SCROLL_EDGES : measure(node))
  }, [])

  return { ref, ...edges }
}

export { useScrollEdges }
